import React, { useState, useCallback } from 'react';
import { clsx } from 'clsx';
import { CheckCircle2, Info, Mail, MapPin, Phone, Smartphone, XCircle } from 'lucide-react';
import { StatusBadge } from '../shared/StatusBadge';
import { formatTime, formatDistance } from '../../utils/dateHelpers';
import { formatUserLabel, getInitials } from '../../utils/mapHelpers';
import type { UserActivityItem } from '../../types/tracking';

const MAX_VISIBLE_ROLES = 3;

interface EmployeeTileProps {
  activity: UserActivityItem;
  isSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
}

/** One row inside the expanded details card. Hides itself when the value
 *  is missing so a sparse profile doesn't render a wall of "—". */
const DetailLine: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
}> = ({ icon, label, value }) => {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="text-rgf-gray-400 mt-0.5 flex-shrink-0">{icon}</span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-rgf-gray-500">{label}</div>
        <div className="text-rgf-gray-800 break-words">{trimmed}</div>
      </div>
    </div>
  );
};

export const EmployeeTile: React.FC<EmployeeTileProps> = React.memo(
  ({ activity, isSelected, onClick }) => {
    const initials = getInitials(activity.name);
    const [rolesExpanded, setRolesExpanded] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);

    const hasOverflow = activity.role.length > MAX_VISIBLE_ROLES;
    const visibleRoles = rolesExpanded ? activity.role : activity.role.slice(0, MAX_VISIBLE_ROLES);
    const hiddenCount = activity.role.length - MAX_VISIBLE_ROLES;

    const toggleRoles = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      setRolesExpanded(prev => !prev);
    }, []);

    // Info button toggles an inline details card below the tile. Clicking
    // it must not bubble to the tile's onClick, which would otherwise
    // change the map selection.
    const toggleDetails = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      setDetailsOpen(prev => !prev);
    }, []);

    const hasAnyDetails =
      !!activity.email?.trim() ||
      !!activity.phone_number?.trim() ||
      !!activity.permanent_address?.trim() ||
      !!activity.district?.trim() ||
      !!activity.status?.trim();

    return (
      <div
        onClick={onClick}
        className={clsx(
          'flex flex-col cursor-pointer transition-colors border-b border-rgf-gray-100',
          isSelected
            ? 'bg-rgf-primary-50 border-l-2 border-l-rgf-primary-500'
            : 'hover:bg-rgf-gray-50'
        )}
      >
        <div className="flex items-center gap-3 px-4 py-3">
        {/* Avatar */}
        <div
          className={clsx(
            'h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold',
            isSelected
              ? 'bg-rgf-primary-600 text-white'
              : 'bg-rgf-primary-100 text-rgf-primary-700'
          )}
        >
          {initials}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span
              className="text-sm font-medium text-rgf-gray-900 truncate"
              title={formatUserLabel(activity.name, activity.employee_id)}
            >
              {formatUserLabel(activity.name, activity.employee_id)}
            </span>
            <div className="flex items-center gap-1 flex-shrink-0">
              <StatusBadge isLive={activity.is_live} />
              {hasAnyDetails && (
                <button
                  type="button"
                  onClick={toggleDetails}
                  aria-label={detailsOpen ? 'Hide user details' : 'Show user details'}
                  aria-expanded={detailsOpen}
                  className={clsx(
                    'p-1 -m-0.5 rounded-md transition-colors',
                    detailsOpen
                      ? 'bg-rgf-primary-100 text-rgf-primary-700'
                      : 'text-rgf-gray-400 hover:bg-rgf-gray-100 hover:text-rgf-gray-700',
                  )}
                  title={detailsOpen ? 'Hide details' : 'Show details'}
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {activity.role.length > 0 && (
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              {visibleRoles.map(r => (
                <span
                  key={r}
                  className="text-xs text-rgf-gray-500 bg-rgf-gray-100 px-1.5 py-0.5 rounded"
                >
                  {r}
                </span>
              ))}
              {hasOverflow && (
                <button
                  type="button"
                  onClick={toggleRoles}
                  className="text-xs text-rgf-primary-600 hover:text-rgf-primary-700 font-medium px-1 py-0.5"
                >
                  {rolesExpanded ? 'Show less' : `+${hiddenCount} more`}
                </button>
              )}
            </div>
          )}

          {activity.device_model && (
            <div className="flex items-center gap-1 mt-0.5 text-xs text-rgf-gray-500">
              <Smartphone className="h-3 w-3 flex-shrink-0" />
              <span className="truncate" title={activity.device_model}>
                {activity.device_model}
              </span>
            </div>
          )}

          <div className="flex items-center gap-3 mt-1 text-xs text-rgf-gray-500">
            {activity.check_in_time && (
              <span>In: {formatTime(activity.check_in_time)}</span>
            )}
            {activity.check_out_time && (
              <span>Out: {formatTime(activity.check_out_time)}</span>
            )}
            {activity.total_distance_km > 0 && (
              <span>{formatDistance(activity.total_distance_km)}</span>
            )}
          </div>
        </div>
        </div>

        {/* Expandable details — toggled by the Info button. Inline so the
            popover can't get clipped by the panel's scroll container, and
            so keyboard navigation flows naturally. */}
        {detailsOpen && hasAnyDetails && (
          <div
            className="px-4 py-3 border-t border-rgf-gray-100 bg-rgf-gray-50 space-y-2"
            onClick={(e) => e.stopPropagation()}
          >
            <DetailLine
              icon={<Mail className="h-3.5 w-3.5" />}
              label="Email"
              value={activity.email}
            />
            <DetailLine
              icon={<Phone className="h-3.5 w-3.5" />}
              label="Phone"
              value={activity.phone_number}
            />
            <DetailLine
              icon={<MapPin className="h-3.5 w-3.5" />}
              label="Address"
              value={activity.permanent_address}
            />
            <DetailLine
              icon={<MapPin className="h-3.5 w-3.5" />}
              label="District"
              value={activity.district}
            />
            <DetailLine
              icon={
                activity.status?.toLowerCase() === 'active' ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-rgf-gray-400" />
                )
              }
              label="Status"
              value={activity.status}
            />
          </div>
        )}
      </div>
    );
  }
);

EmployeeTile.displayName = 'EmployeeTile';
